import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../lib/useAsync';
import { activitiesApi } from '../../api/resources';
import { StatusBadge, PageLoader, ErrorState, EmptyState } from '../../components/ui';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import SubmissionPreview from '../../components/SubmissionPreview';
import { normalizeSubmission } from '../../components/submissionTypes';

/**
 * The activities list endpoint returns published items (from anyone) plus the
 * member's OWN items in every workflow state. ActivityResource now exposes an
 * `author` object ({ id, name }) built from the creator relation, so we filter
 * by author id exactly like MyNews / MyStories do. Filtering by status used to
 * hide the member's own PUBLISHED activities, which is why they could not see
 * anything they had already had published.
 */
export default function MyActivities() {
  const { user } = useAuth();
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => activitiesApi.list({ per_page: 50 }), []);
  // Read-only "how it will look published" preview, any status.
  const [preview, setPreview] = useState(null);

  const mine = (data?.items || []).filter((a) => a.author?.id === user?.id);

  async function handleSubmit(id) {
    try {
      await activitiesApi.submit(id, 'Submitted for review.');
      toast.success('Activity submitted for review.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not submit.');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this activity? This cannot be undone.')) return;
    try {
      await activitiesApi.remove(id);
      toast.success('Activity deleted.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not delete.');
    }
  }

  if (loading) return <PageLoader />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="space-y-6">
      <Seo title="My Activities" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">My Activities</h1>
        <Link to="/member/activities/new" className="btn-primary">
          + New Activity
        </Link>
      </div>

      <p className="text-sm text-ink-muted">
        Every activity you have submitted, from drafts to published. Use Preview to see exactly how
        an activity looks before or after it goes live on the public Activities page.
      </p>

      {mine.length === 0 ? (
        <EmptyState
          title="No activities yet"
          message="Share your club's community work by submitting your first activity."
          icon="🤝"
          action={
            <Link to="/member/activities/new" className="btn-primary">
              Submit an activity
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {mine.map((a) => (
                <tr key={a.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{a.title}</td>
                  <td className="px-5 py-3 text-ink-muted">
                    {a.occurred_on ? formatDate(a.occurred_on) : '—'}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={a.status} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPreview(normalizeSubmission(a, 'activities'))}
                        className="btn-outline px-3 py-1.5 text-xs"
                        aria-label={`Preview ${a.title}`}
                      >
                        Preview
                      </button>
                      {a.status === 'published' ? (
                        <a
                          href={`/activities/${a.slug || a.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost px-3 py-1.5 text-xs"
                        >
                          View
                        </a>
                      ) : (
                        <Link
                          to={`/member/activities/${a.id}/edit`}
                          className="btn-ghost px-3 py-1.5 text-xs"
                        >
                          Edit
                        </Link>
                      )}
                      {(a.status === 'draft' || a.status === 'rejected') && (
                        <button onClick={() => handleSubmit(a.id)} className="btn-gold px-3 py-1.5 text-xs">
                          Submit
                        </button>
                      )}
                      {a.status !== 'published' && (
                        <button
                          onClick={() => handleDelete(a.id)}
                          className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubmissionPreview item={preview} onClose={() => setPreview(null)} readOnly />
    </div>
  );
}
